/**
 * StripeCardVaultForm - Inline card vaulting using Stripe SetupIntent
 * Uses separate fields for card number, expiry, CVC, and ZIP
 * Similar to StripePaymentForm but uses confirmSetup instead of confirmCardPayment
 */
import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { CreditCard, Loader2, Lock, CheckCircle, AlertCircle, Calendar, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Styling for individual Stripe elements to match dark theme
const ELEMENT_OPTIONS = {
  style: {
    base: {
      color: '#ffffff',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSmoothing: 'antialiased',
      fontSize: '16px',
      '::placeholder': {
        color: '#64748b',
      },
      backgroundColor: 'transparent',
    },
    invalid: {
      color: '#ef4444',
      iconColor: '#ef4444',
    },
    complete: {
      color: '#22c55e',
      iconColor: '#22c55e',
    },
  },
};

// Inner form component that uses Stripe hooks
const VaultCardForm = ({ 
  clientId,
  clientSecret,
  onSuccess, 
  onError,
  onCancel,
  disabled = false,
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [postalCode, setPostalCode] = useState('');
  
  // Track completion status of each field
  const [fieldStatus, setFieldStatus] = useState({
    cardNumber: { complete: false, error: null },
    cardExpiry: { complete: false, error: null },
    cardCvc: { complete: false, error: null },
  });

  // Check if all card fields are complete
  const allFieldsComplete = 
    fieldStatus.cardNumber.complete && 
    fieldStatus.cardExpiry.complete && 
    fieldStatus.cardCvc.complete &&
    postalCode.length >= 5;

  // Check for any errors
  const hasError = 
    fieldStatus.cardNumber.error || 
    fieldStatus.cardExpiry.error || 
    fieldStatus.cardCvc.error;

  const handleFieldChange = (field) => (event) => {
    setFieldStatus(prev => ({
      ...prev,
      [field]: {
        complete: event.complete,
        error: event.error ? event.error.message : null,
      }
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    if (!allFieldsComplete) {
      toast.error('Please complete all card details');
      return;
    }

    setProcessing(true);

    try {
      // Get the card number element for setup confirmation
      const cardNumberElement = elements.getElement(CardNumberElement);

      // Confirm the setup with Stripe
      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardNumberElement,
          billing_details: {
            address: {
              postal_code: postalCode,
            },
          },
        },
      });

      if (error) {
        console.error('Stripe setup error:', error);
        
        // Parse error for user-friendly message
        let errorMessage = error.message || 'Card setup failed. Please try again.';
        if (error.code === 'card_declined') {
          errorMessage = 'Your card was declined. Please try a different card.';
        } else if (error.code === 'expired_card') {
          errorMessage = 'Your card has expired. Please use a different card.';
        } else if (error.code === 'incorrect_cvc') {
          errorMessage = 'Invalid CVC code. Please check and try again.';
        } else if (error.code === 'incorrect_zip') {
          errorMessage = 'Invalid ZIP code. Please check and try again.';
        }
        
        if (onError) onError(new Error(errorMessage));
        toast.error(errorMessage);
        setProcessing(false);
        return;
      }

      if (setupIntent.status === 'succeeded') {
        // Save the payment method to the client record
        const completeResponse = await axios.post(
          `${API}/client-portal/${clientId}/complete-card-setup`,
          { payment_method_id: setupIntent.payment_method }
        );

        if (completeResponse.data.success) {
          toast.success('Card saved successfully!');
          if (onSuccess) {
            onSuccess({
              cardLastFour: completeResponse.data.card_last_four,
              cardBrand: completeResponse.data.card_brand,
            });
          }
        } else {
          throw new Error(completeResponse.data.error || 'Failed to save card');
        }
      } else {
        throw new Error(`Setup status: ${setupIntent.status}`);
      }
    } catch (err) {
      console.error('Card vault error:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Failed to save card';
      if (onError) onError(new Error(errorMessage));
      toast.error(errorMessage);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Card Number */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-200 flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-slate-400" />
          Card Number
        </Label>
        <div className={`p-3 bg-slate-700 border rounded-lg transition-colors ${
          fieldStatus.cardNumber.error 
            ? 'border-red-500' 
            : fieldStatus.cardNumber.complete 
              ? 'border-green-500' 
              : 'border-slate-500 focus-within:border-teal-500'
        }`}>
          <CardNumberElement 
            options={ELEMENT_OPTIONS} 
            onChange={handleFieldChange('cardNumber')}
          />
        </div>
        {fieldStatus.cardNumber.error && (
          <p className="text-xs text-red-400 flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            {fieldStatus.cardNumber.error}
          </p>
        )}
      </div>

      {/* Expiry and CVC Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Expiry Date */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-200 flex items-center gap-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            Expiry Date
          </Label>
          <div className={`p-3 bg-slate-700 border rounded-lg transition-colors ${
            fieldStatus.cardExpiry.error 
              ? 'border-red-500' 
              : fieldStatus.cardExpiry.complete 
                ? 'border-green-500' 
                : 'border-slate-500 focus-within:border-teal-500'
          }`}>
            <CardExpiryElement 
              options={ELEMENT_OPTIONS} 
              onChange={handleFieldChange('cardExpiry')}
            />
          </div>
          {fieldStatus.cardExpiry.error && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {fieldStatus.cardExpiry.error}
            </p>
          )}
        </div>

        {/* CVC */}
        <div className="space-y-2">
          <Label className="text-sm font-medium text-slate-200 flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-slate-400" />
            CVC
          </Label>
          <div className={`p-3 bg-slate-700 border rounded-lg transition-colors ${
            fieldStatus.cardCvc.error 
              ? 'border-red-500' 
              : fieldStatus.cardCvc.complete 
                ? 'border-green-500' 
                : 'border-slate-500 focus-within:border-teal-500'
          }`}>
            <CardCvcElement 
              options={ELEMENT_OPTIONS} 
              onChange={handleFieldChange('cardCvc')}
            />
          </div>
          {fieldStatus.cardCvc.error && (
            <p className="text-xs text-red-400 flex items-center gap-1">
              <AlertCircle className="h-3 w-3" />
              {fieldStatus.cardCvc.error}
            </p>
          )}
        </div>
      </div>

      {/* ZIP Code */}
      <div className="space-y-2">
        <Label className="text-sm font-medium text-slate-200">
          ZIP / Postal Code
        </Label>
        <Input
          type="text"
          placeholder="12345"
          value={postalCode}
          onChange={(e) => setPostalCode(e.target.value.replace(/[^a-zA-Z0-9]/g, '').slice(0, 10))}
          className={`bg-slate-700 border text-white placeholder:text-slate-500 ${
            postalCode.length >= 5 ? 'border-green-500' : 'border-slate-500'
          }`}
          disabled={disabled || processing}
          data-testid="vault-zip-input"
        />
      </div>

      {/* Helper text when fields incomplete */}
      {!allFieldsComplete && !hasError && (
        <p className="text-xs text-slate-400 text-center">
          Complete all fields above to save your card
        </p>
      )}

      {/* Security note */}
      <div className="flex items-center justify-center gap-2 text-xs text-slate-500">
        <Lock className="h-3 w-3" />
        <span>Your card details are securely processed by Stripe</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-2">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={processing}
            className="flex-1 border-slate-500 text-slate-300 hover:bg-slate-700"
            data-testid="vault-cancel-button"
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={disabled || processing || !allFieldsComplete || !stripe}
          className={`flex-1 ${
            allFieldsComplete 
              ? 'bg-teal-600 hover:bg-teal-500' 
              : 'bg-slate-600 cursor-not-allowed'
          } text-white font-semibold`}
          data-testid="vault-submit-button"
        >
          {processing ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving Card...
            </>
          ) : (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              Save Card
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

// Main component that wraps with Stripe Elements provider
const StripeCardVaultForm = ({ clientId, onSuccess, onError, onCancel }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initVaultForm = async () => {
      try {
        // Get Stripe config (publishable key)
        const configResponse = await axios.get(`${API}/stripe/config`);
        if (!configResponse.data?.publishable_key) {
          throw new Error('No Stripe publishable key found');
        }
        const stripe = loadStripe(configResponse.data.publishable_key);
        setStripePromise(stripe);

        // Create SetupIntent and get client_secret
        const setupResponse = await axios.post(`${API}/client-portal/${clientId}/create-setup-intent`);
        if (!setupResponse.data?.success || !setupResponse.data?.client_secret) {
          throw new Error(setupResponse.data?.error || 'Failed to initialize card setup');
        }
        setClientSecret(setupResponse.data.client_secret);

      } catch (err) {
        console.error('Failed to initialize card vault form:', err);
        setError(err.response?.data?.detail || err.message || 'Failed to load card form. Please try again.');
        if (onError) onError(err);
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      initVaultForm();
    }
  }, [clientId, onError]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
        <span className="ml-2 text-slate-400">Loading card form...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-red-400 mb-4">{error}</p>
        {onCancel && (
          <Button
            variant="outline"
            onClick={onCancel}
            className="border-slate-500 text-slate-300 hover:bg-slate-700"
          >
            Close
          </Button>
        )}
      </div>
    );
  }

  if (!stripePromise || !clientSecret) {
    return (
      <div className="text-center py-8">
        <AlertCircle className="h-8 w-8 text-amber-400 mx-auto mb-2" />
        <p className="text-amber-400">Card setup unavailable</p>
      </div>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <VaultCardForm 
        clientId={clientId}
        clientSecret={clientSecret}
        onSuccess={onSuccess}
        onError={onError}
        onCancel={onCancel}
      />
    </Elements>
  );
};

export default StripeCardVaultForm;
